import { useCallback } from 'react';
import { MapLocationData } from '@hero/web-components';

export interface UseMapZoomOptions {
  /** Target zoom level (if not provided, will use map's current zoom or default) */
  zoom?: number;
}

/**
 * Hook for smooth zoom animations
 * Handles pan + zoom animations to specific locations
 */
export function useMapZoom(options: UseMapZoomOptions = {}) {
  const { zoom: defaultZoom } = options;

  /**
   * Zoom to a specific location (instant zoom - kept for backward compatibility)
   */
  const zoomToLocation = useCallback(
    (
      map: google.maps.Map | null,
      location: MapLocationData | google.maps.LatLngLiteral,
      zoomLevel?: number,
    ) => {
      if (!map) return;

      const targetPosition = 'cords' in location ? location.cords : location;
      const targetZoom = zoomLevel || defaultZoom || 15;

      map.panTo(targetPosition);
      map.setZoom(targetZoom);
    },
    [defaultZoom],
  );

  /**
   * Smoothly zoom to a location by incrementing zoom level one at a time
   * 150ms delay per zoom level for smooth animation
   */
  const smoothZoomToLocation = useCallback(
    (map: google.maps.Map | null, position: google.maps.LatLngLiteral, targetZoom: number = 15) => {
      if (!map) return;

      const currentZoom = map.getZoom() || 12;
      map.panTo(position);

      if (Math.abs(currentZoom - targetZoom) < 0.1) return;

      const zoomDiff = targetZoom - currentZoom;
      const steps = Math.abs(Math.round(zoomDiff));
      const direction = zoomDiff > 0 ? 1 : -1;

      let currentStep = 0;
      const zoomInterval = setInterval(() => {
        if (currentStep >= steps) {
          clearInterval(zoomInterval);
          map.setZoom(targetZoom);
          return;
        }
        const newZoom = currentZoom + direction * (currentStep + 1);
        map.setZoom(newZoom);
        currentStep++;
      }, 150);
    },
    [],
  );

  return {
    zoomToLocation, // Keep for backward compatibility
    smoothZoomToLocation, // NEW - use this going forward
  };
}
